package com.exura.components.layouts.plan

import com.exura.components.inputs.ModeToggleOption
import com.exura.features.planConstruction.planConstructionLocalization
import com.exura.features.plandefinition.domain.builder.plan.BasePlanBuilderNew
import com.exura.features.plandefinition.domain.model.plan.BasePlan
import com.exura.ui.Assets
import kotlin.math.abs
import kotlin.math.ceil

enum class CellSize {
    DAY, WEEK, MONTH
}

enum class DisplayRange(
    val label: String,
    val durationDays: Int?,
    val cellSize: CellSize
) {
    ONE_WEEK("1 tydzień", 7, CellSize.DAY),
    TWO_WEEKS("2 tygodnie", 14, CellSize.DAY),
    THIRTY_DAYS("30 dni", 30, CellSize.DAY),
    NINETY_DAYS("90 dni", 90, CellSize.WEEK),
    THREE_SIXTY_DAYS("360 dni", 360, CellSize.MONTH),
    WHOLE_PLAN("Cały plan", null, CellSize.DAY), // Dynamic based on plan size
}

data class DisplayConfig(
    val range: DisplayRange,
    val cellSize: CellSize,
    val cellCount: Int,
    val offsetFromDayZero: Int = 0 // Can be negative for preplans
) {
    companion object {
        fun fromRange(
            range: DisplayRange,
            planBuilder: BasePlanBuilderNew<out BasePlan>?,
            offset: Int = 0
        ): DisplayConfig {
            return when (range) {
                DisplayRange.WHOLE_PLAN -> {
                    val (minDay, maxDay) = calculatePlanBounds(planBuilder)
                    val duration = maxDay - minDay + 1
                    
                    val cellSize = when {
                        duration <= 30 -> CellSize.DAY
                        duration <= 180 -> CellSize.WEEK
                        else -> CellSize.MONTH
                    }
                    
                    val cellCount = when (cellSize) {
                        CellSize.DAY -> duration
                        CellSize.WEEK -> ceil(duration / 7.0).toInt()
                        CellSize.MONTH -> ceil(duration / 30.0).toInt()
                    }
                    
                    DisplayConfig(range, cellSize, cellCount, minDay)
                }
                else -> {
                    val cellCount = when (range.cellSize) {
                        CellSize.DAY -> range.durationDays!!
                        CellSize.WEEK -> ceil(range.durationDays!! / 7.0).toInt()
                        CellSize.MONTH -> ceil(range.durationDays!! / 30.0).toInt()
                    }
                    DisplayConfig(range, range.cellSize, cellCount, offset)
                }
            }
        }
        
        private fun calculatePlanBounds(planBuilder: BasePlanBuilderNew<out BasePlan>?): Pair<Int, Int> {
            if (planBuilder == null) return Pair(-30, 0)
            
            var minDay = 0
            var maxDay = 0
            
            // Check collections
            planBuilder.collections().forEach { collection ->
                val collectionStart = collection.relativeDay
                val collectionEnd = collection.absoluteEndDay() ?: collectionStart
                
                if (collectionStart < minDay) minDay = collectionStart
                if (collectionEnd > maxDay) maxDay = collectionEnd
                
                // Check series within collections
                collection.series.forEach { series ->
                    val seriesAbsoluteStart = series.relativeDay?.plus(collectionStart)
                    val seriesAbsoluteEnd = seriesAbsoluteStart?.let { start ->
                        series.durationInDays()?.let { start + it - 1 }
                    }
                    
                    if (seriesAbsoluteStart != null && seriesAbsoluteStart < minDay) {
                        minDay = seriesAbsoluteStart
                    }
                    if (seriesAbsoluteEnd != null && seriesAbsoluteEnd > maxDay) {
                        maxDay = seriesAbsoluteEnd
                    }
                }
            }
            
            // Check direct series
            planBuilder.directSeries().forEach { series ->
                val seriesStart = series.relativeDay
                val seriesEnd = seriesStart?.let { start ->
                    series.durationInDays()?.let { start + it - 1 }
                }
                
                if (seriesStart != null && seriesStart < minDay) minDay = seriesStart
                if (seriesEnd != null && seriesEnd > maxDay) maxDay = seriesEnd
            }
            
            // Add some padding if plan is very small
            if (maxDay - minDay < 7) {
                minDay -= 3
                maxDay += 3
            }
            
            return Pair(minDay, maxDay)
        }
    }
    
    fun getDisplayDayRange(): Pair<Int, Int> {
        val startDay = offsetFromDayZero
        val endDay = when (cellSize) {
            CellSize.DAY -> startDay + cellCount - 1
            CellSize.WEEK -> startDay + (cellCount * 7) - 1
            CellSize.MONTH -> startDay + (cellCount * 30) - 1
        }
        return Pair(startDay, endDay)
    }
    
    fun navigateForward(): DisplayConfig {
        val step = when (cellSize) {
            CellSize.DAY -> cellCount
            CellSize.WEEK -> cellCount * 7
            CellSize.MONTH -> cellCount * 30
        }
        return copy(offsetFromDayZero = offsetFromDayZero + step)
    }
    
    fun navigateBackward(): DisplayConfig {
        val step = when (cellSize) {
            CellSize.DAY -> cellCount
            CellSize.WEEK -> cellCount * 7
            CellSize.MONTH -> cellCount * 30
        }
        // Allow navigation into negative territory
        return copy(offsetFromDayZero = offsetFromDayZero - step)
    }
}

internal val DISPLAY_RANGE_OPTIONS = listOf(
    ModeToggleOption(
        DisplayRange.ONE_WEEK,
        "1 tydzień",
        Assets.IconsUntiled.PUBLIC_PLAN_ORDER_RANGE_OPTION_WEEK
    ),
    ModeToggleOption(
        DisplayRange.TWO_WEEKS,
        "2 tygodnie",
        Assets.IconsUntiled.PUBLIC_PLAN_ORDER_RANGE_OPTION_WEEK
    ),
    ModeToggleOption(
        DisplayRange.THIRTY_DAYS,
        "30 dni",
        Assets.IconsUntiled.PUBLIC_PLAN_ORDER_RANGE_OPTION_MONTH
    ),
    ModeToggleOption(
        DisplayRange.NINETY_DAYS,
        "90 dni",
        Assets.IconsUntiled.PUBLIC_PLAN_ORDER_RANGE_OPTION_MONTH
    ),
    ModeToggleOption(
        DisplayRange.THREE_SIXTY_DAYS,
        "360 dni",
        Assets.IconsUntiled.PLACEHOLDER
    ),
    ModeToggleOption(
        DisplayRange.WHOLE_PLAN,
        "Cały plan",
        Assets.IconsUntiled.PLACEHOLDER
    ),
)
